import { AnimatePresence, motion } from 'framer-motion';
import { AuthFormContent } from './auth-form.content';
import { VerifyOtpContent } from './verify-otp.content';
import { ThirdPartyAuthContent } from '@/components/auth/modals/third-party-auth.modal';
import { useThirdPartyAuth } from '@/contexts/third-party.context';
import React from 'react';

interface AuthFlowContentProps {
	email: string;
	setEmail: (email: string) => void;
	error?: string;
	setError: (error: string) => void;
	isLoading: boolean;
	isWTN: boolean;
	isVerifyingOtp: boolean;
	setIsVerifyingOtp: (value: boolean) => void;
	handleRequestOtp: (e: React.FormEvent) => void;
	handleVerifyOtp: (code: string) => Promise<void>;
	handleResendOtp: () => Promise<void>;
	onGoogleMobileAuth: () => Promise<void>;
	onAppleMobileAuth: () => Promise<void>;
	onGoogleWebAuth: () => Promise<void>;
	onGoogleExternalBrowserHandoff: () => Promise<void>;
	onCopyExternalBrowserLink: () => Promise<void>;
	onGoogleWebAuthResponse: (response: any) => Promise<void>;
	onAppleWebAuth: () => Promise<void>;
	isXInAppBrowser: boolean;
	xInAppBrowserPlatform: 'android' | 'ios' | 'unknown';
	showXBrowserHandoff: boolean;
	isPopUp?: boolean;
	onClose?: () => void;
	onOpenChange?: (open: boolean) => void;
	autoFocusEmail?: boolean;
}

export function AuthFlowContent({
	email,
	setEmail,
	error,
	setError,
	isLoading,
	isWTN,
	isVerifyingOtp,
	setIsVerifyingOtp,
	handleRequestOtp,
	handleVerifyOtp,
	handleResendOtp,
	onGoogleMobileAuth,
	onAppleMobileAuth,
	onGoogleWebAuth,
	onGoogleExternalBrowserHandoff,
	onCopyExternalBrowserLink,
	onGoogleWebAuthResponse,
	onAppleWebAuth,
	isXInAppBrowser,
	xInAppBrowserPlatform,
	showXBrowserHandoff,
	isPopUp,
	onClose,
	onOpenChange,
	autoFocusEmail = true,
}: AuthFlowContentProps) {
	const { toastState, onComplete } = useThirdPartyAuth();
	const showThirdPartyInline = isPopUp && !!toastState;

	const handleBack = () => {
		setIsVerifyingOtp(false);
		setError('');
	};

	return (
		<AnimatePresence mode="wait">
			{showThirdPartyInline ? (
				<motion.div
					key="third-party-result"
					initial={{ opacity: 0, x: 20 }}
					animate={{ opacity: 1, x: 0 }}
					exit={{ opacity: 0, x: -20 }}
					transition={{ duration: 0.3 }}
					className="w-full h-full"
				>
					<ThirdPartyAuthContent
						toast={toastState!}
						{...(onComplete ? { onClose: onComplete } : {})}
					/>
				</motion.div>
			) : isVerifyingOtp ? (
				<motion.div
					key="verify-otp"
					initial={{ opacity: 0, x: 20 }}
					animate={{ opacity: 1, x: 0 }}
					exit={{ opacity: 0, x: -20 }}
					transition={{ duration: 0.3 }}
					className={`w-full h-full ${isPopUp ? 'p-6' : ''}`}
				>
					<VerifyOtpContent
						email={email}
						onVerify={handleVerifyOtp}
						onResendOtp={handleResendOtp}
						onBack={handleBack}
						onClose={onClose}
						isLoading={isLoading}
						error={error}
						isPopUp={isPopUp}
					/>
				</motion.div>
			) : (
				<motion.div
					key="form"
					initial={{ opacity: 0, x: -20 }}
					animate={{ opacity: 1, x: 0 }}
					exit={{ opacity: 0, x: 20 }}
					transition={{ duration: 0.3 }}
					className={`w-full h-full ${isPopUp ? 'pt-8 px-6' : ''}`}
				>
					<AuthFormContent
						email={email}
						setEmail={setEmail}
						isLoading={isLoading}
						handleSubmit={handleRequestOtp}
						onGoogleMobileAuth={onGoogleMobileAuth}
						onAppleMobileAuth={onAppleMobileAuth}
						onGoogleWebAuth={onGoogleWebAuth}
						onGoogleExternalBrowserHandoff={onGoogleExternalBrowserHandoff}
						onCopyExternalBrowserLink={onCopyExternalBrowserLink}
						onGoogleWebAuthResponse={onGoogleWebAuthResponse}
						onAppleWebAuth={onAppleWebAuth}
						isWTN={isWTN}
						isXInAppBrowser={isXInAppBrowser}
						xInAppBrowserPlatform={xInAppBrowserPlatform}
						showXBrowserHandoff={showXBrowserHandoff}
						error={error}
						setError={setError}
						isPopUp={isPopUp}
						onOpenChange={onOpenChange}
						autoFocusEmail={autoFocusEmail}
					/>
				</motion.div>
			)}
		</AnimatePresence>
	);
}
